import { type CustomQuestion, type Dayjs, type ExportedBlueprint, type Question, type RepeaterLikeQuestion, type SortOrder, type VTagType } from '@vartion/ui'

import type { TranslationLanguage } from '~/stores/translation.ts'
import type { ClientFilter } from './composables/useClients.ts'

// Helper type to convert union to intersection.
type UnionToIntersection<U> = (U extends unknown ? (k: U) => void : never) extends (k: infer I) => void ? I : never

// Generic type helper to add relationship fields based on "with" parameter.
type WithRelations<Relations, T extends readonly string[] = []> = T extends readonly []
  ? {} // eslint-disable-line @typescript-eslint/no-empty-object-type
  : UnionToIntersection<
      {
        [K in T[number]]: K extends keyof Relations ? Relations[K] : never
      }[T[number]]
    >

export interface BaseScreeningCategory {
  name: 'regulatory watchlists' | 'peps' | 'news' | 'other'
  label: string
  vendors: string[]
  sourceName: CaseSourceName
  price: number
}

export interface ScreeningCategory extends BaseScreeningCategory {
  isActive: boolean
}

export type ModuleName = 'onboarding' | 'transaction_monitoring'

export type { MentionableModelValue as MentionableComment } from '@vartion/ui'

export type AlertRuleType =
  | 'balance_change_absolute'
  | 'bank_account_age'
  | 'bank_account_balance'
  | 'bank_account_dormancy'
  | 'close_monitoring'
  | 'bank_account_type'
  | 'client_audit_count'
  | 'client_age'
  | 'client_bank_accounts_count'
  | 'client_country'
  | 'client_is_new'
  | 'client_risk'
  | 'client_risk_indicators'
  | 'client_profession'
  | 'client_standard_industry_classification_risk'
  | 'client_type'
  | 'tax_haven_country_profile_risk'
  | 'transaction_amount'
  | 'transaction_count'
  | 'unique_counter_party_count'
  | 'transaction_counter_party_bank_account_country'
  | 'transaction_description_keywords'
  | 'transaction_direction'
  | 'transaction_type'
  | 'blocked_countries'
  | 'incoming_outgoing_ratio'
  | 'manual'
  | 'peer_group_deviation'

export type AlertRuleScope = 'client' | 'bank_account'

export interface BaseAlertRule<TType = unknown | AlertRuleType, TValue = unknown> {
  scope: AlertRuleScope
  type: TType
  value: TValue
}

export interface MinMaxValue {
  min: number | null
  max: number | null
}

export interface MinMaxRule<TType extends unknown | AlertRuleType = unknown> extends BaseAlertRule {
  type: TType
  value: MinMaxValue
}

export interface MultiMinMaxRule<TType extends unknown | AlertRuleType = unknown, AdditionalProperties extends object = object> extends BaseAlertRule {
  type: TType
  value: { match_logic: 'any' | 'all'; conditions: (MinMaxValue & AdditionalProperties)[] }
}

export interface InRule<TType extends AlertRuleType | unknown = unknown, TValueType = string> extends BaseAlertRule {
  type: TType
  value: { in: TValueType[]; not_in: TValueType[] }
}

export type TransactionDirection = 'incoming' | 'outgoing'

export type BalanceChangeAbsoluteAlertRule = MultiMinMaxRule<'balance_change_absolute', { currency: string }>
export type BankAccountAgeAlertRule = MinMaxRule<'bank_account_age'>
export type BankAccountBalanceAlertRule = MultiMinMaxRule<'bank_account_balance', { currency: string }>
export type BankAccountDormancyAlertRule = InRule<'bank_account_dormancy', boolean>
export type BankAccountTypeAlertRule = InRule<'bank_account_type'>
export type ClientAuditCountAlertRule = MultiMinMaxRule<'client_audit_count', { type: string[] }>
export type ClientAgeAlertRule = MinMaxRule<'client_age'>
export type ClientBankAccountsCountAlertRule = MinMaxRule<'client_bank_accounts_count'>
export type ClientCountryAlertRule = InRule<'client_country'>
export type ClientIsNewAlertRule = InRule<'client_is_new', boolean>
export type ClientRiskRule = InRule<'client_risk', RiskLevel>
export type ClientRiskIndicatorsRule = InRule<'client_risk_indicators'>
export type ClientRoleAlertRule = InRule<'client_profession'>
export type ClientStandardIndustryClassificationRiskRule = InRule<'client_standard_industry_classification_risk', number | string>
export type ClientTypeAlertRule = InRule<'client_type'>
export type TransactionCounterPartyCountryAlertRule = InRule<'transaction_counter_party_bank_account_country'>
export type TaxHavenCountryProfileRiskRule = InRule<'tax_haven_country_profile_risk'>
export type TransactionAmountAlertRule = MultiMinMaxRule<'transaction_amount', { currency: string }> & { value: { direction: TransactionDirection | 'any' } }
export type TransactionCountAlertRule = MinMaxRule<'transaction_count'>
export type UniqueCounterPartyCountAlertRule = MinMaxRule<'unique_counter_party_count'> & { value: { direction: TransactionDirection | 'any' } }
export type TransactionDescriptionKeywordsAlertRule = InRule<'transaction_description_keywords'>
export type TransactionDirectionAlertRule = InRule<'transaction_direction'>
export type TransactionTypeAlertRule = InRule<'transaction_type'>
export type BlockedCountriesAlertRule = InRule<'blocked_countries', string>
export type IncomingOutgoingRatioAlertRule = MinMaxRule<'incoming_outgoing_ratio'>

export type CreateAlertRule =
  | BalanceChangeAbsoluteAlertRule
  | BankAccountAgeAlertRule
  | BankAccountBalanceAlertRule
  | BankAccountDormancyAlertRule
  | BankAccountTypeAlertRule
  | ClientAuditCountAlertRule
  | ClientAgeAlertRule
  | ClientBankAccountsCountAlertRule
  | ClientCountryAlertRule
  | ClientIsNewAlertRule
  | ClientRiskRule
  | ClientRiskIndicatorsRule
  | ClientRoleAlertRule
  | ClientStandardIndustryClassificationRiskRule
  | ClientTypeAlertRule
  | TransactionCounterPartyCountryAlertRule
  | TaxHavenCountryProfileRiskRule
  | TransactionAmountAlertRule
  | TransactionCountAlertRule
  | UniqueCounterPartyCountAlertRule
  | TransactionDescriptionKeywordsAlertRule
  | TransactionDirectionAlertRule
  | TransactionTypeAlertRule
  | BlockedCountriesAlertRule
  | IncomingOutgoingRatioAlertRule

export type AlertRule = CreateAlertRule & {
  id: number
  alert_template_id: number
  created_at: string
  updated_at: string
}

export interface CreateAlertTemplate {
  id: number
  name: string
  priority: null | number
  timings: AlertTemplateTiming[]
  time_range: null | 0 | 1 | 7 | 28 | 90 | 180 | 365
  alert_frequency: null | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'half-yearly' | 'yearly'
  user_id: null | number
  group_id: null | number
  status: 'active' | 'inactive'
  exemption_list_ids: number[]
  reason: string
  rules: CreateAlertRule[]
}

export type AlertTemplateTiming = 'transaction' | 'bank_account' | 'bank_account_balance' | 'eod' | 'client_audit'

interface AlertTemplateRelations {
  rules: { rules: AlertRule[] }
  exemptionLists: { exemption_lists: ExemptionList[] }
  previousTemplate: { previous_template: AlertTemplate<['rules', 'exemptionLists']> | null }
}

export type AlertTemplate<With extends (keyof AlertTemplateRelations)[] = []> = {
  id: number
  sequence_id: string
  organization_id: number
  name: string
  priority: number
  timings: AlertTemplateTiming[]
  time_range: null | 0 | 1 | 7 | 28 | 90 | 180 | 365
  alert_frequency: null | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'half-yearly' | 'yearly'
  user_id: null | number
  group_id: null | number
  status: 'active' | 'inactive'
  reason: string
  created_by_user_id: null | number
  created_by_user_name: string
  created_at: string
  updated_at: string
} & WithRelations<AlertTemplateRelations, With>

export type AlertTemplateDetailed = AlertTemplate<['rules', 'exemptionLists']>

export type AlertStatus = 'open' | 'false_positive' | 'suspicious' | 'under_investigation' | 'escalated' | 'closed'

export interface AlertAuditAttachment {
  id: number
  alert_audit_id: number
  file_name: string
  extension: string
  created_at: string
  updated_at: string
}

export interface Alert {
  id: number
  alert_template_id: null | number
  organization_id: number
  client_id: number
  sequence_id: string
  name: string
  priority: number
  status: AlertStatus
  data: { reasons: AlertReason[] }
  created_at: string
  updated_at: string
  frequency_starts_at: null | string
  frequency_ends_at: null | string
  time_range_starts_at: string
  time_range_ends_at: string
  alert_template: null | AlertTemplate<['rules']>
  bank_accounts: BankAccount[]
  transactions_count: number
  user_id: null | number
  group_id: null | number
  client: Client
  investigation_date?: string
  latest_comment?: CommentAlertAudit
  is_resolved: boolean
  is_stale: boolean
}

type NonRuleAlertReasonType = 'close_monitoring' | 'manual' | 'peer_group_deviation'

export interface BaseAlertReason<TType extends Exclude<AlertRuleType, NonRuleAlertReasonType>, TValue = unknown> {
  bank_account_id?: number
  rule_id: number
  type: TType
  value: TValue
}

export interface BaseNonRuleAlertReason<TType extends NonRuleAlertReasonType, TValue = unknown> {
  type: TType
  value: TValue
}

export interface BalanceChangeAbsoluteAlertReason extends BaseAlertReason<'balance_change_absolute'> {
  value: { amount: number; currency: string; from: number; to: number; bank_account: string }
}

export interface BankAccountAgeAlertReason extends BaseAlertReason<'bank_account_age'> {
  value: number
}

export interface BankAccountBalanceAlertReason extends BaseAlertReason<'bank_account_balance'> {
  value: { amount: number; currency: string }
}

export interface BlockedCountriesAlertReason extends BaseAlertReason<'blocked_countries'> {
  value: string[]
}

export interface ClientAuditCountAlertReason extends BaseAlertReason<'client_audit_count'> {
  value: { count: number; type: string[] }
}

export interface ClientAgeAlertReason extends BaseAlertReason<'client_age'> {
  value: number
}

export interface ClientBankAccountsCountAlertReason extends BaseAlertReason<'client_bank_accounts_count'> {
  value: number
}

export interface ClientCountryAlertReason extends BaseAlertReason<'client_country'> {
  value: string[]
}

export interface ClientIsNewAlertReason extends BaseAlertReason<'client_is_new'> {
  value: boolean
}

export interface ClientRiskAlertReason extends BaseAlertReason<'client_risk'> {
  value: RiskLevel
}

export interface ClientRiskIndicatorsAlertReason extends BaseAlertReason<'client_risk_indicators'> {
  value: string[]
}

export interface ClientRoleAlertReason extends BaseAlertReason<'client_profession'> {
  value: string
}

export interface ClientStandardIndustryClassificationRiskAlertReason extends BaseAlertReason<'client_standard_industry_classification_risk'> {
  value: number | string
}

export interface ClientTypeAlertReason extends BaseAlertReason<'client_type'> {
  value: string
}

export interface CounterPartyBankAccountCountryAlertReason extends BaseAlertReason<'transaction_counter_party_bank_account_country'> {
  value: string[]
}

export interface TransactionAmountAlertReason extends BaseAlertReason<'transaction_amount'> {
  condition_index: number
  value: { amount: number; currency: string }
}

export interface TransactionCountAlertReason extends BaseAlertReason<'transaction_count'> {
  value: number
}

export interface UniqueCounterPartyCountAlertReason extends BaseAlertReason<'unique_counter_party_count'> {
  value: number
}

export interface TransactionDirectionAlertReason extends BaseAlertReason<'transaction_direction'> {
  value: string
}

export interface TransactionTypeAlertReason extends BaseAlertReason<'transaction_type'> {
  value: string
}

export interface TransactionCloseMonitoringAlertReason extends BaseNonRuleAlertReason<'close_monitoring'> {
  value: boolean
}

export interface TransactionManualAlertReason extends BaseNonRuleAlertReason<'manual'> {
  value: string
}

export interface PeerGroupDeviationAlertReason extends BaseNonRuleAlertReason<'peer_group_deviation'> {
  value: {
    field: string
    client_value: number
    peer_group_value: number
    allowed_deviation_percentage: number
  }
}

export interface IncomingOutgoingRatioAlertReason extends BaseAlertReason<'incoming_outgoing_ratio'> {
  value: number
}

export type AlertReason =
  | BalanceChangeAbsoluteAlertReason
  | BankAccountAgeAlertReason
  | BankAccountBalanceAlertReason
  | BlockedCountriesAlertReason
  | ClientAuditCountAlertReason
  | ClientAgeAlertReason
  | ClientBankAccountsCountAlertReason
  | ClientCountryAlertReason
  | ClientIsNewAlertReason
  | ClientRiskAlertReason
  | ClientRiskIndicatorsAlertReason
  | ClientRoleAlertReason
  | ClientStandardIndustryClassificationRiskAlertReason
  | ClientTypeAlertReason
  | CounterPartyBankAccountCountryAlertReason
  | TransactionAmountAlertReason
  | TransactionCountAlertReason
  | UniqueCounterPartyCountAlertReason
  | TransactionTypeAlertReason
  | TransactionCloseMonitoringAlertReason
  | TransactionManualAlertReason
  | PeerGroupDeviationAlertReason
  | IncomingOutgoingRatioAlertReason

export interface Batch {
  id: string
  created_at: string
  finished_at: string | null
  name: string
  progress: number
  total_jobs: number
  failed_jobs: number
  processed_jobs: number
}

export interface QueueJobTypeCount {
  type: string
  count: number
}

export type QueueJobState = 'ready' | 'delayed' | 'reserved'

export interface QueueJobSample {
  limit: number
  sampled: Record<QueueJobState, number>
  types: ({ type: string } & Record<QueueJobState, number>)[]
}

export interface QueueSnapshot {
  name: string
  connection: string
  ready: number
  delayed: number
  reserved: number
  total: number
  ready_sample: QueueJobTypeCount[]
  delayed_sample: QueueJobTypeCount[]
  processed_history: { time: number; processed: number }[]
}

export interface QueueBatchSnapshot {
  id: string
  name: string
  total_jobs: number
  pending_jobs: number
  failed_jobs: number
  processed_jobs: number
  progress: number
  created_at: number
  finished_at: number | null
  cancelled_at: number | null
}

export interface QueueDashboardSnapshot {
  sample_size: number
  captured_at: string
  queues: QueueSnapshot[]
  batches: {
    summary: { active: number; finished: number; cancelled: number; with_failures: number }
    recent: QueueBatchSnapshot[]
  }
}

export interface IntelligentCheckJob {
  type: string
  caseId: number
  filteredSourceIds: number[]
}

export interface BankAccountBalance {
  id: number
  organization_id: number
  transaction_id: number | null
  bank_account_number: string
  bank_account_swift_code: string
  balance: number
  balance_at: string
  created_at: string
  updated_at: string
}

export interface BankAccount {
  id: number
  organization_id: number
  client_id: number
  name: string
  account_number: string
  swift_code: string
  bank_name: null | string
  active: boolean
  currency: string
  account_type: string
  account_creation_date: null | string
  created_at: string
  updated_at: string
  latest_transaction: null | Transaction
  latest_balance: null | BankAccountBalance
}

export type CreateBankAccount = Pick<BankAccount, 'name' | 'account_number' | 'swift_code' | 'bank_name' | 'currency' | 'account_type' | 'active' | 'account_creation_date'>
export type EditBankAccount = Pick<BankAccount, 'id' | 'name' | 'account_number' | 'swift_code' | 'bank_name' | 'currency' | 'account_type' | 'active' | 'account_creation_date'>

export interface ExemptionList {
  id: number
  organization_id: number
  name: string
  description: string
  created_at: string
  updated_at: string
}

export type CreateExemption = Pick<Exemption, 'exemption_list_id' | 'name' | 'bank_account_number' | 'swift_code' | 'bank_name'>

export interface Exemption {
  id: number
  organization_id: number
  exemption_list_id: number
  name: string
  bank_account_number: string
  swift_code: string
  bank_name: null | string
  created_at: string
  updated_at: string
}

export type Confidence = 'Relative' | 'Exact'

export interface CaseAuditBucket {
  key: string
  count: number
  year: number
  month: number
  day: number
  description: string
  by_app_employee: boolean
  created_at: string
  type: string
  user_id: number
  user_name: string
}

export interface BaseCaseAudit {
  id: number
  case_id: number
  user_id: number
  case_hit_id: null | number
  user_name: string
  description: string
  type: null | string
  data: null | Record<never, never>
  by_app_employee: boolean
  ip_address: string
  created_at: string
  updated_at: string
}

export interface AssignedCaseAudit extends BaseCaseAudit {
  type: 'assigned'
  data: null | {
    assignee_type: 'user' | 'group'
    assignee_id: number
  }
}

export interface CommentAddedCaseAudit extends BaseCaseAudit {
  type: 'comment added'
  data: null
}

export interface ConfidenceChangeCaseAudit extends BaseCaseAudit {
  type: 'confidence change'
  data: { confidence: Confidence }
}

export interface CreatedCaseAudit extends BaseCaseAudit {
  type: 'created'
  data: null
}

export interface DeletedCaseAudit extends BaseCaseAudit {
  type: 'deleted'
  data: null
}

export interface HitUpdatedCaseAudit extends BaseCaseAudit {
  type: 'hit updated'
  data: null
}

export interface HyperlinkSearchCaseAudit extends BaseCaseAudit {
  type: 'hyperlink search'
  data: { name: string }
}

export interface PassportVerificationStatusChangeCaseAudit extends BaseCaseAudit {
  type: 'passport verification status change'
  data: { status: string }
}

export interface ServiceAddedCaseAudit extends BaseCaseAudit {
  type: 'service added'
  data: { service: string }
}

export interface CaseSourceSearchedCaseAudit extends BaseCaseAudit {
  type: 'case source searched'
  data: {
    data_source_id: number
  }
}

export interface StatusChangeCaseAudit extends BaseCaseAudit {
  type: 'status change'
  data: { status: string; reason?: string }
}

export interface InputChangeCaseAudit extends BaseCaseAudit {
  type: 'input change'
  data: null
}

export interface HitResolvedCaseAudit extends BaseCaseAudit {
  type: 'hit resolved'
  data: null | {
    included: boolean
    user_risk_score?: number
  }
}

export interface MonitoringFrequenciesChangedCaseAudit extends BaseCaseAudit {
  type: 'monitoring frequencies changed'
  data: {
    old: Partial<Record<MonitoringFrequencyKey, MonitoringFrequency>>
    new: Partial<Record<MonitoringFrequencyKey, MonitoringFrequency>>
  }
}

export type CaseAudit =
  | AssignedCaseAudit
  | CommentAddedCaseAudit
  | ConfidenceChangeCaseAudit
  | CreatedCaseAudit
  | DeletedCaseAudit
  | HitResolvedCaseAudit
  | HitUpdatedCaseAudit
  | HyperlinkSearchCaseAudit
  | InputChangeCaseAudit
  | PassportVerificationStatusChangeCaseAudit
  | ServiceAddedCaseAudit
  | CaseSourceSearchedCaseAudit
  | StatusChangeCaseAudit
  | MonitoringFrequenciesChangedCaseAudit

export type AssetType = null | 'aircraft' | 'vessel' | 'cryptowallet'
export type CaseGender = '' | 'Male' | 'Female'

export interface CreateCaseForm {
  name: string
  external_identifier: string
  aliases: string[]
  additional_terms: string[]
  excluded_terms: string[]
  nationalities: string[]
  country_of_birth: string
  country_of_residence: string
  date_of_birth: string
  gender: null | CaseGender
  company_number: null | string
  address: null | string
  country: null | string
  asset_type: AssetType
  identifier: string
  client_ids: number[]
  user_id: null | number
  group_id: null | number
  confidence: Confidence
  match_on_connections: boolean
  status: CaseStatus
  sources: CaseSourceName[]
  type: CaseType
}

export interface CaseForm {
  name: string
  external_identifier: null | string
  aliases: null | string[]
  additional_terms: null | string[]
  nationalities: null | string[]
  country_of_birth: null | string
  country_of_residence: null | string
  date_of_birth: null | string
  gender: null | CaseGender
  address: null | string
  company_number: null | string
  country: null | string
  identifier: null | string
  asset_type: AssetType
}

export const CASE_STATUSES = ['Preview', 'In review', 'Monitored', 'Archived', 'On hold'] as const

export type CaseStatus = (typeof CASE_STATUSES)[number]

export const CASE_STATUS_COLORS = { Preview: 'brand-300', 'In review': 'secondary', Monitored: 'success', Archived: 'info', Active: 'primary', 'On hold': 'error' } as Record<
  string,
  VTagType
>

export const ALERT_STATUS_COLORS = {
  open: 'primary',
  closed: 'info',
  false_positive: 'success',
  under_investigation: 'warning',
  suspicious: 'error',
  escalated: 'warning',
} as Record<AlertStatus, VTagType>

export const CASE_TYPES = ['Business', 'Person', 'Asset'] as const
export type CaseType = (typeof CASE_TYPES)[number]

export type MonitoringFrequency = 0 | 1 | 7 | 28 | 90 | 180 | 365

interface CaseModelRelations {
  hits: { hits: CaseHit[] }
  hitComments: { hit_comments: HitComment[] }
  reportSchedule: { report_schedule: ReportSchedule | null }
  passportVerification: { passport_verification: PassportVerification }
  hitsUpdatedCount: { hits_updated_count: number }
  hitCounts: { hit_counts: null | HitCounts }
  'onboardingFormQuestions.children': { onboarding_form_questions: OnboardingFormQuestion<['children']>[] }
  onboardingCaseModelVerification: { onboarding_case_model_verification: OnboardingCaseModelVerification | null }
  caseUsers: { case_users: CaseUser[] }
  caseGroups: { case_groups: CaseGroup[] }
  clients: { clients: Pick<Client, 'id' | 'name' | 'type'>[] }
  latestOnHoldAudit: { latest_on_hold_audit: StatusChangeCaseAudit | null }
  labels: { labels: Label[] }
}

export type CaseModel<With extends (keyof CaseModelRelations)[] = [], T extends CaseType = CaseType> = {
  id: number
  uuid: string
  external_identifier: string | null
  name: string
  type: T
  confidence: Confidence
  match_on_connections: boolean
  status: CaseStatus
  origin: 'person-check' | 'business-check' | 'country-check' | 'hyperlink' | 'import' | 'public-api'
  user_id: number | null
  user: User
  group_id: number | null
  group: Group
  organization_id: number
  organization: Organization
  risk: number | null
  unresolved_risk: number | null
  created_at: string
  updated_at: string
  searched_at: string | null
  deleted_at: string | null
  hubspot_portal_object_id: null | string
  salesforce_object_id: null | string
  clients?: { id: number; name: string }[]
  hits_unresolved_count: number
  hits_total_count: number

  resolve_progress: number
  services: CaseServiceName[]
  sources: CaseSource[]
  case_users: CaseUser[]
  case_groups: CaseGroup[]
  description: null | string
  monitoring_frequency_sanctions: MonitoringFrequency
  monitoring_frequency_peps: MonitoringFrequency
  monitoring_frequency_news: MonitoringFrequency
  monitoring_frequency_enforcements: MonitoringFrequency
  monitoring_frequency_other: MonitoringFrequency
  aliases: null | string[]
  additional_terms: null | string[]
  excluded_terms: null | string[]
  country_of_birth: null | string
  country_of_residence: null | string
  date_of_birth: null | string
  gender: null | CaseGender
  nationalities: null | string[]
  address: null | string
  company_number: null | string
  country: null | string
  identifier: null | string
  asset_type: null | string
} & WithRelations<CaseModelRelations, With>

export type CaseModelDetailed<T extends CaseType = CaseType> = CaseModel<
  ['clients', 'hits', 'hitComments', 'reportSchedule', 'passportVerification', 'latestOnHoldAudit', 'labels'],
  T
>

export type FormattedCaseModel = Omit<CaseModel<['clients']>, 'group'> & {
  assignee: string
  group: string
  riskLabel: string | null
}

export interface CaseGroup {
  id: number
  case_id: number
  group_id: number
  created_at: string
  updated_at: string
}

export interface CaseUser {
  id: number
  case_id: number
  user_id: number
  created_at: string
  updated_at: string
}

export interface CaseSource {
  active: boolean
  id: number
  case_id: number
  data_source_id: number
  data_source: CaseSourceDataSource
  searched_at: string | null
  succeeded: boolean
}

export type CaseModelSearchMode = 'full' | 'update' | 'none'

export const CASE_SOURCE_NAMES = ['enforcements', 'news', 'other', 'peps', 'sanctions'] as const

export type CaseSourceName = (typeof CASE_SOURCE_NAMES)[number]

export interface ClientCollaborator {
  id: number
  client_id: number
  user_id: number
  created_at: string
  updated_at: string
}

export interface ApprovalTemplate {
  id: number
  organization_id: number
  name: string
  approval_steps: ApprovalStep[]
  supported_risk_levels: RiskLevel[]
}

export interface ApprovalStep {
  id: number
  approvable_id: number
  approvable_type: string
  approved: boolean
  user_id: number
  user_name: string
  comment: string | null
  created_at: string
  updated_at: string
  organization_functions: OrganizationFunction[]
  notifiables: User[]

  organization_function_ids: number[]
  active?: boolean
  canApprove?: boolean
}

export interface ClientReviewPolicyReminder {
  days: number
  timing: 'before' | 'after'
}

export interface ClientReviewPolicy {
  id: number
  organization_id: number
  name: string
  type: ClientReviewType
  trigger_type: 'client_risk_change' | 'included_hit_category' | null
  trigger_hit_categories: CaseSourceName[]
  client_risk_levels: RiskLevel[]
  onboarding_form_template_id: number | null
  frequency: number
  frequency_unit: 'days' | 'months' | 'years'
  create_days_before_due_date: number
  reminders: ClientReviewPolicyReminder[]
  created_at: string
  updated_at: string
}

export type ClientReviewType = 'onboarding' | 'periodic' | 'event_driven' | 'manual'
export const CLIENT_REVIEW_PENDING_STATUSES = ['not_started'] as const
export const CLIENT_REVIEW_ACTIVE_STATUSES = ['awaiting_information', 'awaiting_approval'] as const
export const CLIENT_REVIEW_OPEN_STATUSES = [...CLIENT_REVIEW_PENDING_STATUSES, ...CLIENT_REVIEW_ACTIVE_STATUSES] as const
export const CLIENT_REVIEW_HISTORICAL_STATUSES = ['approved', 'rejected', 'cancelled'] as const
export const CLIENT_REVIEW_STATUSES = [...CLIENT_REVIEW_OPEN_STATUSES, ...CLIENT_REVIEW_HISTORICAL_STATUSES] as const
export type ClientReviewStatus = (typeof CLIENT_REVIEW_STATUSES)[number]

export type ClientRiskMethod = 'highest' | 'weighted'

export interface ClientRiskCalculation {
  method: ClientRiskMethod
  screening_risk: RiskLevel | null
  onboarding_risk: RiskLevel | null
  screening_value: number | null
  onboarding_value: number | null
  screening_weight: number | null
  onboarding_weight: number | null
  effective_screening_weight: number | null
  effective_onboarding_weight: number | null
  weighted_value: number | null
  calculated_risk: RiskLevel | null
}

export interface ClientReviewCase {
  id: number
  client_review_id: number
  case_id: number | null
  name: string
  type: CaseType
  uuid: string
  risk: number | null
  created_at: string
  updated_at: string
}

interface ClientReviewRelations {
  onboardingForms: { onboarding_forms: OnboardingFormInternal<['user', 'contacts', 'questions']>[] }
  clientReviewCases: { client_review_cases: ClientReviewCase[] }
  approvalSteps: { approval_steps: ApprovalStep[] }
  approvalStartedAudit: { approval_started_audit: ApprovalStartedClientAudit | null }
  riskChangeAudit: { risk_change_audit: CalculatedClientRiskChangedClientAudit | ProfessionalJudgementChangedClientAudit | null }
  cancellationAudit: { cancellation_audit: ClientReviewUpdatedClientAudit | null }
}

export type ClientReview<With extends (keyof ClientReviewRelations)[] = []> = {
  id: number
  organization_id: number
  client_id: number
  onboarding_form_ids?: number[]
  name: string
  type: ClientReviewType
  status: ClientReviewStatus
  approval_template_id: number | null
  risk_change_audit_id: number | null
  approval_started_audit_id: number | null
  cancellation_audit_id: number | null
  onboarding_risk: RiskLevel | null
  pending_professional_judgement_risk: RiskLevel | null
  pending_risk_calculation?: ClientRiskCalculation
  professional_judgement_change_requested: boolean
  professional_judgement_reason: string | null
  report_path: string | null
  client_review_policy_id: number | null
  initial_onboarding_form_template_id: number | null
  reminders: (ClientReviewPolicyReminder & { sent: boolean })[] | null
  due_date: string | null
  started_at: string | null
  completed_at: string | null
  created_at: string
  updated_at: string
} & WithRelations<ClientReviewRelations, With>

interface OnboardingFormRelations {
  user: { user: User }
  contacts: { contacts: ClientContact[] }
  questions: { questions: OnboardingFormQuestion[] }
}

export type OnboardingForm<With extends (keyof OnboardingFormRelations)[] = []> = {
  id: number
  external_name: string
  status: 'in progress' | 'submitted by client' | 'completed' | 'declined'
  organization_id: number
  client_id: number
  client?: Pick<Client, 'id' | 'name'>
  external_questions_total: number
  external_questions_open: number
  contacts?: ClientContact[]
  blueprint: ExportedBlueprint
  organization_theme?: OrganizationTheme
  latest_form_returned_audit?: ClientAudit
  latest_approval_step_rejected_audit?: ClientAudit
  report_path?: string
  created_at: string
  updated_at: string
} & WithRelations<OnboardingFormRelations, With>

export type OnboardingFormInternal<With extends (keyof OnboardingFormRelations)[] = []> = OnboardingForm<With> & {
  client?: Client
  client_review_id: number
  client_review?: ClientReview
  assignee_user_id: number | null
  name: string
  questions_total: number
  questions_open: number
}

interface OnboardingFormQuestionRelations {
  children: { children: OnboardingFormQuestion[] }
}

export type OnboardingFormQuestion<With extends (keyof OnboardingFormQuestionRelations)[] = []> = {
  id: number
  onboarding_form_id: number
  blueprint_question_id: number
  data: Question | CustomOnboardingFormQuestion
  filled_in: boolean
  created_at: string
  updated_at: string
  comments: OnboardingFormQuestionComment[]
  identity_verification?: OnboardingFormQuestionIdentityVerification
  risk: null | string
  overridden_risk: null | string
  case_id: null | number
} & WithRelations<OnboardingFormQuestionRelations, With>

export interface OnboardingFormQuestionComment {
  id: number
  onboarding_form_id: number
  onboarding_form_question_id: number
  user_id: number
  user_name: string
  comment: string
  created_at: string
  updated_at: string
}

export interface OnboardingRiskConfig {
  id: number
  organization_id: number
  weight_very_low: number
  weight_low: number
  weight_moderate: number
  weight_high: number
  weight_very_high: number
  breakpoint_very_low: number
  breakpoint_low: number
  breakpoint_moderate: number
  breakpoint_high: number
  created_at: string
  updated_at: string
}

export interface TransactionMonitoringRiskConfig {
  id: number
  organization_id: number
  account_creation_age_amount: number
  risk_indicators: RiskIndicator[]
  tax_haven_country_profile: Record<string, number | '' | null>
  blocked_countries: string[]
  created_at: string
  updated_at: string
}

export interface RiskIndicator {
  label: string
  risk: number
}

export type CurrencyQuestion = CustomQuestion<
  'currency',
  {
    answer: null | string
  }
>

export type CountryQuestion = CustomQuestion<
  'country',
  {
    answer: null | string | string[]
    allow_multiple_answers: boolean
  }
>

export type PhoneQuestion = CustomQuestion<
  'phone',
  {
    answer: null | string
  }
>

export type EmailQuestion = CustomQuestion<
  'email',
  {
    answer: null | string
  }
>

export type PersonEntityQuestion = CustomQuestion<
  'person-entity',
  RepeaterLikeQuestion & {
    allow_multiple_persons: boolean
    require_verification: boolean
  }
>

export type BusinessEntityQuestion = CustomQuestion<
  'business-entity',
  RepeaterLikeQuestion & {
    allow_multiple_businesses: boolean
  }
>

export type IdentityVerificationQuestion = CustomQuestion<
  'identity-verification',
  {
    answer: null | boolean
  }
>

export interface OnboardingFormQuestionIdentityVerification {
  id: number
  uuid: string
  onboarding_form_question_id: number
  oauth_token_expires_at: string
  auto_capture_sdk_token: string
  face_verify_sdk_token: string
  face_verify_transaction_id: string
  face_verify_status: null | 'created' | 'completed' | 'declined'
  face_verify_created_at: string
  id_verify_transaction_id: string
  id_verify_status: null | 'created' | 'completed' | 'declined'
  id_verify_created_at: string
  full_name: string | null
  name: string | null
  given_names: string | null
  surname: string | null
  date_of_birth: string | null
  date_of_issuance: string | null
  date_of_expiry: string | null
  document_type: string | null
  document_number: string | null
  gender: string | null
  issuing_state_or_organization: string | null
  nationality: string | null
  personal_government_id: string | null
  place_of_birth: string | null
  residence_permit_type: string | null
  created_at: string
  updated_at: string
}

export type CustomOnboardingFormQuestion =
  | CurrencyQuestion
  | CountryQuestion
  | PhoneQuestion
  | EmailQuestion
  | PersonEntityQuestion
  | BusinessEntityQuestion
  | IdentityVerificationQuestion

export interface EmailTemplate {
  id: number
  organization_id: number
  name: string
  subject: EmailTemplateElement[]
  body: EmailTemplateElement[]
  created_at: string
  updated_at: string
}

export type EmailTemplateElement =
  | {
      type: 'newline'
    }
  | {
      type: 'text'
      text: string
    }
  | {
      type: 'variable'
      text: string
      variable_name: string
      bold?: true
    }
  | {
      type: 'button'
      text: string
      id: string // Identifier to determine dynamic URLs.
    }
  | {
      type: 'image'
      text: string
      id: string // Identifier to determine dynamic image sources.
    }

export type EmailTemplateField = Extract<EmailTemplateElement, { type: 'variable' } | { type: 'button' } | { type: 'image' }> & { multiple?: boolean }

export interface OrganizationTheme {
  id: number
  organization_id: number
  company_name: string
  company_logo?: string | File
  font_family: null | string
  font_color: null | string
  background_color: null | string
  color_primary: null | string
  font_color_primary_button: null | string
  color_secondary: null | string
  font_color_secondary_button: null | string
  privacy_url: null | string
}

export interface OnboardingFormTemplate {
  id: number
  name: string
  external_name: string
  blueprint: ExportedBlueprint
  organization_id: number
  created_at: string
  updated_at: string
}
export interface TransactionMaximumAmountAmount {
  id: number
  currency: string
  amount: number
  transaction_maximum_amount_id: number
  created_at: string
  updated_at: string
}

export interface TransactionMaximumAmount {
  id: number
  client_type: string
  is_default: boolean
  organization_id: number
  amounts: TransactionMaximumAmountAmount[]
  created_at: string
  updated_at: string
}

export interface OnboardingCaseModelVerification {
  id: number
  case_id: number
  session_url: string
  status: string
  created_at: string
  updated_at: string
}

export interface GeoIP {
  city: string
  continent: string
  country: string
  currency: string
  default: boolean
  ip: string
  iso_code: string
  lat: number
  lon: number
  postal_code: string
  state: string
  state_name: string
  timezone: string
}

export interface NERType {
  count: number
  coverage: number
  value: string
  GENDER?: string
  occurrence?: string
}

export type RiskClassification = 'very low' | 'low' | 'moderate' | 'high' | 'very high'

export interface HitComment {
  id: number
  case_id: number
  hit_id: number
  user_id: number
  user_name: string
  source: string
  comment: string
  created_at: string
  updated_at: string
}

export interface HitCounts {
  negative: HitCountsResolution
  positive: HitCountsResolution
  unresolved: HitCountsResolution
}

export interface HitCountsResolution {
  enforcements: number
  news: number
  other: number
  peps: number
  sanctions: number
}

export interface HitDataPosition {
  position: string
  url?: string
  listed_at?: string | null
  listed_until?: string | null
  inactive?: boolean
  status?: string | null
}

export type HitDataFunctions = {
  type: string
  value: string
}[]

export interface PepRole {
  pep_position: string
  pep_role_bio: string
  pep_role_term_start_date?: string
  pep_role_term_end_date?: string
  pep_role_status?: string
  pep_role_level: string
}

export interface Characteristic {
  type?: string
  value?: string
}

export interface Officer {
  type: string | { value: string }
  name: string
  position: string
  listed_at: string
  listed_until: string
}

export interface HitDataAddress {
  text?: string
  place?: string
  country?: string
  country_code?: string
  formatted?: string
  geometry?:
    | {
        lng?: number
        lon?: number
        lat: number
      }
    | [number, number]
  confidence?: number
}

export interface HitDataRisk {
  score: number
  probability: string
  minSetProbability: RiskClassification
  impact: RiskClassification
  classification: RiskClassification
  components: Record<
    string,
    {
      score: number
      description: string
      classification: RiskClassification | 'disabled'
    }
  >
}

export interface HitHighlight {
  'aliases.name'?: string[]
  'persons.value'?: string[]
  'organizations.value'?: string[]
  'locations.value'?: string[]
  connections?: Record<string, string[]>
  // There could be more, we can add them as needed.
}

export interface HitDataSourceObject {
  name: string
  url?: string
  country?: string
}

export interface HitData {
  [key: string]: unknown
  aliases: {
    name: string
    type: string
  }[]
  addresses?: HitDataAddress[]
  adverse?: Record<string, number>
  archived?: boolean
  birth_dates?: { date: string }[]
  characteristics?: Characteristic[]
  country?: string
  data_directory_external_identifier?: string | null
  // Input field -> input value -> matched field.
  explanation: Record<
    string,
    Record<
      string,
      Record<
        string,
        {
          match: string
          matchDetails?: string[]
        }
      >
    >
  >
  external_identifier?: string | null
  gender?: CaseGender | { value: CaseGender; predicted?: boolean | null } | null
  highlight?: HitHighlight
  home_company?: {
    company_number?: string
    name: string
  }
  id: string | number
  industries?: null | string[]
  industry_types: {
    code: string
    description: string
    type: string
    code_scheme_id: string
  }[]
  language?: string
  locations?: NERType[]
  name: string
  nationalities?: Partial<{ country_code: string; country: string }>[] | null
  negative_events?: string[]
  officers?: Officer[]
  organizations?: NERType[]
  pep_roles?: PepRole[]
  functions?: HitDataFunctions
  persons?: NERType[]
  positions?: HitDataPosition[]
  publish_date?: null | string
  score: number
  source: string | string[] | HitDataSourceObject | HitDataSourceObject[]
  title?: string
  risk?: HitDataRisk
  summary?: string
  type?: {
    value: string
    predicted?: boolean
  }
  checked_at?: string
  url?: string
  vendor?: string
  case_hit?: CaseHit
}

export type HitResolution = 'unresolved' | 'positive' | 'negative'

export interface CaseHit {
  id: number
  case_id: number
  implicit_feedback: number | null
  resolution: HitResolution
  data: HitData
  data_source_id: number
  update: null | HitData
  update_changes: null | {
    updated: Record<keyof HitData, { new: unknown; old: unknown }>
    added: Record<keyof HitData, { new: unknown }>
    removed: Record<keyof HitData, { old: unknown }>
  }
  update_risk_delta: null | number
  removed_from_source: boolean
  comments: HitComment[]
  user_risk_score: number | null
  profile_id: string | null
  created_at: string
  updated_at: string
  resolved_at: string | null
  similar_data_ids: string[]
  source: CaseSourceName
  labels: Label[]
}

export const CLIENT_STATUS_COLORS = { Archived: 'info', Active: 'primary', 'In review': 'secondary', 'On hold': 'error' } as Record<string, VTagType>

export type RiskLevel = 'very_low' | 'low' | 'moderate' | 'high' | 'very_high'

interface ClientRelations {
  assignee: { assignee: User }
  contacts: { contacts: ClientContact[] }
  transactions_count: { transactions_count: number }
  clients: { clients: Pick<Client, 'id' | 'name' | 'type'>[] }
  client_standard_industry_classifications: { client_standard_industry_classifications: ClientStandardIndustryClassification[] }
  labels: { labels: Label[] }
  currentClientReview: { current_client_review: ClientReview | null }
  latestClientReview: { latest_client_review: ClientReview | null }
  historicalClientReviews: { historical_client_reviews: ClientReview[] }
}

export interface ClientContact {
  id: number
  client_id: number
  first_name: string
  last_name: string
  email: string
  created_at: string
  updated_at: string
}

export type Client<With extends (keyof ClientRelations)[] = []> = {
  id: number
  identifier: string
  name: string
  status: 'Archived' | 'Active' | 'In review' | 'On hold'
  onboarding_risk: RiskLevel | null
  current_client_review?: ClientReview | null
  latest_client_review?: ClientReview | null
  description: string
  assignee_user_id: number | null
  organization_id: number
  collaborators: User[]
  created_at: string
  updated_at: string
  email: string | null
  phone_number: string | null
  bank_accounts_count?: number
  unresolved_high_risk_hits_count?: number
  transaction_maximum_amount_id: number | null
  address: string | null
  incorporation_date: string | null
  country: string | null
  company_number: string | null
  account_creation_date: string | null
  date_of_birth: string | null
  nationalities: string[] | null
  type: 'Person' | 'Business' | null
  gender: 'male' | 'female' | 'non-binary' | null
  risk_indicators: string[] | null
  country_risk: number | null
  screening_risk: RiskLevel | null
  calculated_client_risk: RiskLevel | null
  professional_judgement_risk: RiskLevel | null
  overall_client_risk: RiskLevel | null
  calculated_client_risk_updated_at: string | null
  estimated_incoming_amount: null | number
  estimated_incoming_currency: null | string
  estimated_outgoing_amount: null | number
  estimated_outgoing_currency: null | string
  profession: null | string
  close_monitoring: boolean
  cash_incoming_currency: string | null
  cash_incoming_amount: number | null
  cash_outgoing_currency: string | null
  cash_outgoing_amount: number | null
  cash_percentage: number | null
  transaction_count: number | null
  transaction_volume_incoming: number | null
  transaction_volume_outgoing: number | null
  transaction_count_cross_border: number | null
  transaction_volume_cross_border_incoming: number | null
  transaction_volume_cross_border_outgoing: number | null
  transaction_countries: string[] | null
} & WithRelations<ClientRelations, With>

export interface ClientNote {
  id: number
  created_at: string
  updated_at: string
  comment: string
  files: ClientNoteFile[]
  user_id: number
  alerts_count?: number
  alerts_max_id?: number | null
  transactions_count?: number
  files_count?: number
}

export interface ClientNoteFile {
  id: number
  client_note_id: number
  original_name: string
  extension: string
  created_at: string
  updated_at: string
}

export interface ClientWithBankAccounts extends Client {
  bank_accounts: BankAccount[]
}

export interface ClientDetailed extends Client {
  cases_count: number
  total_count: number
  unresolved_count: number
  resolve_progress: number
  labels?: Label[]
}

export interface ClientDetailedWithStandardIndustryClassifications extends ClientDetailed, Client<['client_standard_industry_classifications']> {
  labels: Label[]
}

export type ClientAuditType = ClientAudit['type']

export interface ClientAuditBucket {
  key?: string
  client_id: number
  count: number
  year: number
  month: number
  day: number
  description?: string
  by_app_employee: boolean
  created_at: string
  type: ClientAuditType
  user_id: number | null
  user_name: string | null
}

export interface BaseAlertAudit {
  id: number
  alert_id: number
  user_id: number
  user_name: string
  type: null | string
  data: null | Record<never, never>
  by_app_employee: boolean
  ip_address: string
  created_at: string
  updated_at: string
}

export interface CreatedAlertAudit extends BaseAlertAudit {
  type: 'created'
  data: null
}

export interface StatusChangedAlertAudit extends BaseAlertAudit {
  type: 'status changed'
  data: {
    old_status: string
    new_status: string
  }
}

export interface AssigneeChangedAlertAudit extends BaseAlertAudit {
  type: 'assignee changed'
  data: {
    assignee_type: 'user' | 'group' | null
    assignee_id: number | null
    assignee_name?: string | null
  }
}

export interface CommentAlertAudit extends BaseAlertAudit {
  type: 'comment'
  data: {
    comment: string
  }
  attachments?: AlertAuditAttachment[]
}

export type AlertAudit = CreatedAlertAudit | StatusChangedAlertAudit | AssigneeChangedAlertAudit | CommentAlertAudit

export interface AlertAuditBucket {
  key?: string
  id: number
  count: number
  data: Record<string, any> | null
  year: number
  month: number
  day: number
  by_app_employee: boolean
  created_at: string
  type: string
  user_id: number
  user_name: string
  description?: string
}

export interface BaseClientAudit {
  id: number
  client_id: number
  user_id: number | null
  user_name: string | null
  type: string
  data: null | Record<string, any>
  changes: null | Record<string, { old: unknown; new: unknown }>
  reason: string | null
  by_app_employee: boolean
  ip_address: string
  created_at: string
  updated_at: string
}

export interface AlertCreatedClientAudit extends BaseClientAudit {
  type: 'alert created'
  data: {
    name: string[]
  }
}

export interface AlertResolvedClientAudit extends BaseClientAudit {
  type: 'alert resolved'
  data: {
    alert_name: string
  }
}

export interface ApprovalStartedClientAudit extends BaseClientAudit {
  type: 'approval started'
  data: {
    client_review_id: number
    client_risk: RiskLevel | null
    configured_default_template_id: number | null
    configured_default_template_name: string | null
    selected_template_id: number
    selected_template_name: string
    selected_template_supported_risk_levels?: RiskLevel[]
    block_lower_risk_approval_templates: boolean
    justification: string | null
  }
}

export interface ApprovalStepApprovedClientAudit extends BaseClientAudit {
  type: 'approval step approved'
  data: { approval_step_number: number }
}

export interface ApprovalStepRejectedClientAudit extends BaseClientAudit {
  type: 'approval step rejected'
  data: { approval_step_number: number }
}

export interface ApprovalCompletedClientAudit extends BaseClientAudit {
  type: 'approval completed'
  data: { client_review_id: number; client_review_name: string }
}

export interface ClientReviewCreatedClientAudit extends BaseClientAudit {
  type: 'client review created'
  data: {
    client_review_id: number
    name: string
    type: ClientReview['type']
    status: ClientReview['status']
  } & (
    | { trigger_type?: undefined }
    | ({ client_review_policy_id: number; due_date: string } & (
        | { trigger_type: 'client_risk_change'; old_overall_client_risk: RiskLevel | null; new_overall_client_risk: RiskLevel }
        | { trigger_type: 'included_hit_category'; case_id: number; case_name: string; case_uuid: string; case_hit_ids: number[]; hit_categories: CaseSourceName[] }
      ))
  )
}

export interface ClientReviewUpdatedClientAudit extends BaseClientAudit {
  type: 'client review updated'
  data: { client_review_id: number; name: string; type: ClientReview['type']; comment?: string }
  changes: Record<string, { old: unknown; new: unknown }>
}

export interface CaseLinkedClientAudit extends BaseClientAudit {
  type: 'case linked'
  data: { case_id: number; case_name: string }
}

export interface CaseUnlinkedClientAudit extends BaseClientAudit {
  type: 'case unlinked'
  data: { case_id: number; case_name: string }
}

export interface ClientLinkedClientAudit extends BaseClientAudit {
  type: 'client linked'
  data: { client_id: number; client_name: string }
}

export interface ClientUnlinkedClientAudit extends BaseClientAudit {
  type: 'client unlinked'
  data: { client_id: number; client_name: string }
}

export interface BankAccountUpdatedClientAudit extends BaseClientAudit {
  type: 'bank account updated'
  data: {
    bank_account_id: number
    bank_account_name: string
    bank_account_number: string
    swift_code: string
  }
}

export interface CloseMonitoringChangedClientAudit extends BaseClientAudit {
  type: 'close monitoring changed'
  data: { close_monitoring: boolean }
}

export interface StatusChangeClientAudit extends BaseClientAudit {
  type: 'status change'
  changes: null | { status: { old: string | null; new: string } }
}

export interface CreatedClientAudit extends BaseClientAudit {
  type: 'created'
  data: null
}

export interface ClientFieldsChangedClientAudit extends BaseClientAudit {
  type: 'client fields changed'
  changes: Record<string, { old: unknown; new: unknown }>
}

export interface TransactionProfileChangedClientAudit extends BaseClientAudit {
  type: 'transaction profile changed'
  changes: Record<string, { old: unknown; new: unknown }>
}

export interface FormCompletedClientAudit extends BaseClientAudit {
  type: 'form completed'
  data: {
    onboarding_form_id: number
    onboarding_form_name: string
  }
  changes: null | { status: { old: OnboardingForm['status'] | null; new: OnboardingForm['status'] } }
}

export interface FormSubmittedClientAudit extends BaseClientAudit {
  type: 'form submitted'
  data: {
    onboarding_form_id: number
    onboarding_form_name: string
  }
  changes: null | { status: { old: OnboardingForm['status'] | null; new: OnboardingForm['status'] } }
}

export interface FormCreatedClientAudit extends BaseClientAudit {
  type: 'form created'
  data: {
    onboarding_form_id: number
    onboarding_form_name: string
  }
}

export interface FormRemovedClientAudit extends BaseClientAudit {
  type: 'form removed'
  data: {
    onboarding_form_id: number
    onboarding_form_name: string
    client_review_id: number
    client_review_name: string
  }
}

export interface FormReturnedClientAudit extends BaseClientAudit {
  type: 'form returned'
  data: {
    onboarding_form_id: number
    onboarding_form_name: string
    comment: string
  }
  changes: null | { status: { old: OnboardingForm['status'] | null; new: OnboardingForm['status'] } }
}

export interface FormAssigneeChangedClientAudit extends BaseClientAudit {
  type: 'form assignee changed'
  data: {
    onboarding_form_id: number
    onboarding_form_name: string
  }
  changes: {
    assignee: {
      old: { id: number; name: string } | null
      new: { id: number; name: string } | null
    }
  }
}

export interface OnboardingStartedClientAudit extends BaseClientAudit {
  type: 'onboarding started'
  data: null
}

export interface UserRiskScoreChangedClientAudit extends BaseClientAudit {
  type: 'user risk score changed'
  data: {
    old_risk: RiskClassification
    new_risk: RiskClassification
    new_risk_is_overruled: boolean
  }
}

export interface CalculatedClientRiskChangedClientAudit extends BaseClientAudit {
  type: 'calculated client risk changed'
  data: {
    screening_risk: RiskLevel | null
    onboarding_risk: RiskLevel | null
    professional_judgement_risk?: RiskLevel | null
    calculation?: ClientRiskCalculation
  }
  changes: {
    calculated_client_risk: { old: RiskLevel | null; new: RiskLevel | null }
    overall_client_risk?: { old: RiskLevel | null; new: RiskLevel | null }
  }
}

export interface ProfessionalJudgementChangedClientAudit extends BaseClientAudit {
  type: 'professional judgement changed'
  data: {
    screening_risk: RiskLevel | null
    onboarding_risk: RiskLevel | null
    calculated_client_risk: RiskLevel | null
  }
  changes: Record<'professional_judgement_risk' | 'overall_client_risk', { old: RiskLevel | null; new: RiskLevel | null }>
  reason: string
}

// Field change audit types.
export interface AssigneeChangedClientAudit extends BaseClientAudit {
  type: 'assignee changed'
  data: null | [] | Record<string, never>
  changes: null | {
    assignee: {
      old: { id: number; name: string | null } | null
      new: { id: number; name: string | null } | null
    }
  }
}

export interface CollaboratorsChangedClientAudit extends BaseClientAudit {
  type: 'collaborators changed'
  changes: Record<'collaborators', { old: unknown; new: unknown }>
}

export interface LabelsChangedClientAudit extends BaseClientAudit {
  type: 'labels changed'
  changes: Record<'labels', { old: unknown; new: unknown }>
}

export interface AddressChangedClientAudit extends BaseClientAudit {
  type: 'address changed'
  data: {
    address: string | null
  }
}

export interface EmailChangedClientAudit extends BaseClientAudit {
  type: 'email changed'
  data: {
    email: string | null
  }
}

export interface PhoneNumberChangedClientAudit extends BaseClientAudit {
  type: 'phone number changed'
  data: {
    phone_number: string | null
  }
}

export interface DateOfBirthChangedClientAudit extends BaseClientAudit {
  type: 'date of birth changed'
  data: {
    date_of_birth: string | null
  }
}

export interface GenderChangedClientAudit extends BaseClientAudit {
  type: 'gender changed'
  data: {
    gender: string | null
  }
}

export interface IdentityVerificationDeletedClientAudit extends BaseClientAudit {
  type: 'identity verification deleted'
  data: {
    onboarding_form_id: number
    onboarding_form_name: string
  }
}

export interface ProfessionChangedClientAudit extends BaseClientAudit {
  type: 'profession changed'
  data: {
    profession: string | null
  }
}

export interface SicCodeChangedClientAudit extends BaseClientAudit {
  type: 'sic codes changed'
  data: {
    sic_codes: string[]
  }
}

export interface CountryChangedClientAudit extends BaseClientAudit {
  type: 'country changed'
  data: {
    country: string | null
  }
}

export interface NationalitiesChangedClientAudit extends BaseClientAudit {
  type: 'nationalities changed'
  data: {
    nationalities: string[] | null
  }
}

export interface DateOfIncorporationChangedClientAudit extends BaseClientAudit {
  type: 'date of incorporation changed'
  data: {
    incorporation_date: string | null
  }
}

export interface CompanyNumberChangedClientAudit extends BaseClientAudit {
  type: 'company number changed'
  data: {
    company_number: string | null
  }
}

export interface AccountCreationDateChangedClientAudit extends BaseClientAudit {
  type: 'account creation changed'
  data: {
    account_creation_date: string | null
  }
}

export interface RiskIndicatorsChangedClientAudit extends BaseClientAudit {
  type: 'risk indicators changed'
  data: {
    risk_indicators: string[] | null
  }
}

export interface BankAccountCreatedClientAudit extends BaseClientAudit {
  type: 'bank account added'
  data: {
    bank_account_id: number
    bank_account_name: string
    bank_account_number: string
    swift_code: string | null
  }
}

export interface NoteAddedClientAudit extends BaseClientAudit {
  type: 'note added'
  data: {
    client_note_id: number
    comment: string
  }
}

export interface AttachmentAddedClientAudit extends BaseClientAudit {
  type: 'attachment added'
  data: { client_note_id: number; client_note_file_id: number; name: string }
}

export interface OnboardingCompletedClientAudit extends BaseClientAudit {
  type: 'onboarding completed'
  data: []
}

export interface OnboardingRejectedClientAudit extends BaseClientAudit {
  type: 'onboarding rejected'
  data: []
}

export interface ContactRemovedFromFormClientAudit extends BaseClientAudit {
  type: 'contact removed from onboarding form'
  data: {
    onboarding_form_id: number
    onboarding_form_name: string
    external_user_name: string
  }
}

export interface ContactAssignedToFormClientAudit extends BaseClientAudit {
  type: 'contact assigned to onboarding form'
  data: {
    onboarding_form_id: number
    onboarding_form_name: string
    external_user_name: string
  }
}

export type ClientAudit =
  | AccountCreationDateChangedClientAudit
  | AddressChangedClientAudit
  | AlertCreatedClientAudit
  | AlertResolvedClientAudit
  | ApprovalStartedClientAudit
  | ApprovalCompletedClientAudit
  | ApprovalStepApprovedClientAudit
  | ApprovalStepRejectedClientAudit
  | AssigneeChangedClientAudit
  | AttachmentAddedClientAudit
  | BankAccountCreatedClientAudit
  | BankAccountUpdatedClientAudit
  | CalculatedClientRiskChangedClientAudit
  | CaseLinkedClientAudit
  | CaseUnlinkedClientAudit
  | ClientFieldsChangedClientAudit
  | ClientLinkedClientAudit
  | ClientUnlinkedClientAudit
  | ClientReviewCreatedClientAudit
  | ClientReviewUpdatedClientAudit
  | CloseMonitoringChangedClientAudit
  | CollaboratorsChangedClientAudit
  | CompanyNumberChangedClientAudit
  | CountryChangedClientAudit
  | CreatedClientAudit
  | DateOfBirthChangedClientAudit
  | DateOfIncorporationChangedClientAudit
  | EmailChangedClientAudit
  | ContactAssignedToFormClientAudit
  | ContactRemovedFromFormClientAudit
  | FormAssigneeChangedClientAudit
  | FormCompletedClientAudit
  | FormCreatedClientAudit
  | FormRemovedClientAudit
  | FormReturnedClientAudit
  | FormSubmittedClientAudit
  | GenderChangedClientAudit
  | LabelsChangedClientAudit
  | IdentityVerificationDeletedClientAudit
  | NationalitiesChangedClientAudit
  | NoteAddedClientAudit
  | OnboardingCompletedClientAudit
  | OnboardingRejectedClientAudit
  | OnboardingStartedClientAudit
  | PhoneNumberChangedClientAudit
  | ProfessionalJudgementChangedClientAudit
  | ProfessionChangedClientAudit
  | RiskIndicatorsChangedClientAudit
  | SicCodeChangedClientAudit
  | StatusChangeClientAudit
  | TransactionProfileChangedClientAudit
  | UserRiskScoreChangedClientAudit

export interface ClientStandardIndustryClassification {
  id: number
  client_id: number
  standard_industry_classification_id: number
  standard_industry_classification: null | StandardIndustryClassification
  code: string
  created_at: string
  updated_at: string
}

export interface DataSource {
  id: number
  name: string
  source: CaseSourceName
  vendor: string
  active: boolean
  deactivated_at: null | string
  created_at: null | string
  organization_id: number
  type: string
  updated_at: null | string
}

export type DataDirectoryCategory = 'sanctions' | 'enforcements' | 'peps'

export type DataDirectoryEntryType = 'Business' | 'Person'

export interface DataDirectoryEntry {
  id: number
  name: string
  data: HitData
  external_identifier: string
  updated_at: string
}

export interface DataDirectoryEntryAlias {
  name: string
  type: string
}

export interface DataDirectoryEntryAddress {
  country_code: string
  text: string
}

export interface DataDirectoryEntryNationality {
  country_code: string
}

export interface DataDirectoryEntryCharacteristic {
  type: string
  value: string
}

export interface DataDirectoryEntryBirthDate {
  date: string
}

export interface DataDirectoryEntryBirthPlace {
  country_code: string
  place: string
}

export interface DataDirectoryEntryContactDetail {
  type: string
  value: string
}

export interface DataDirectoryEntryDocument {
  title: string
  type: string
  url: string
}

export interface DataDirectoryEntryImage {
  url: string
}

export interface DataDirectoryEntryIdentifier {
  country_code: string
  type: string
  value: string
}

export interface DataDirectoryEntryConnection {
  name: string
  type: {
    value: 'entity' | 'person' | null
  }
}

export interface DataDirectoryEntryForm {
  addresses: DataDirectoryEntryAddress[]
  aliases: DataDirectoryEntryAlias[]
  birth_dates: DataDirectoryEntryBirthDate[]
  birth_places: DataDirectoryEntryBirthPlace[]
  characteristics: DataDirectoryEntryCharacteristic[]
  connections: DataDirectoryEntryConnection[]
  contact_details: DataDirectoryEntryContactDetail[]
  death_dates: DataDirectoryEntryBirthDate[]
  documents: DataDirectoryEntryDocument[]
  external_identifier: string
  gender: {
    value: 'Female' | 'Male' | null
  }
  identifiers: DataDirectoryEntryIdentifier[]
  images: DataDirectoryEntryImage[]
  listed_at: string
  listed_until: string
  measures: string
  name: string
  nationalities: DataDirectoryEntryNationality[]
  occupation: string
  reason: string
  sanction_type: string
  summary: string
  title: string
  type?: {
    value: DataDirectoryEntryType
  }
  url: string
}

export interface DataDirectory {
  id: number
  name: string
  category: DataDirectoryCategory
  created_at: string
  updated_at: string
}

export type DataDirectoryAuditType =
  | 'data directory created'
  | 'data directory renamed'
  | 'data directory deleted'
  | 'data directory entries created'
  | 'data directory entry updated'
  | 'data directory entry archived'
  | 'data directory entry unarchived'
  | 'data directory entry deleted'
  | 'data directory entries imported'
  | 'data directory entries exported'

export interface DataDirectoryAuditData {
  data_directory_id: number
  data_directory_name: string
  entry_id?: number
  external_identifier?: string | null
  entries?: { entry_id: number; external_identifier: string | null }[]
  entry_ids?: number[]
  count?: number
  created?: number
  updated?: number
  skipped?: number
  old?: Record<string, unknown>
  new?: Record<string, unknown>
  filters?: Record<string, unknown>
}

export interface DataDirectoryAudit {
  id: number
  organization_id: number
  data_directory_id: number
  user_id: number
  user_name: string
  created_at: string
  description: string
  type: DataDirectoryAuditType
  data: DataDirectoryAuditData
  by_app_employee: boolean
  ip_address: string
}

export interface DataDirectoryAuditBucket {
  key?: string
  count: number
  year: number
  month: number
  day: number
  by_app_employee: boolean
  created_at: string
  type: DataDirectoryAuditType
  user_id: number
  user_name: string
  description?: string
}

export type CaseSourceDataSource = Pick<DataSource, 'id' | 'source' | 'vendor' | 'active' | 'deactivated_at'>

export type Locale = 'en' | 'en-gb' | 'es' | 'fr' | 'nl' | 'ru' | 'de' | 'pt' | 'it' | 'el' | 'hi' | 'ro' | 'tr'

export interface Meta {
  currentPage: number
  from: number
  pageCount: number
  pageSize: number
  to: number
  total: number
}

export interface Monitor {
  id: number
  case_id: number
  new_hits_count: number
  should_report: boolean
  created_at: string
  updated_at: string
}

export interface MonitorReport {
  id: number
  user_id: number
  cases_count: number
  cases_with_findings_count: number
  new_hits_count: number
  created_at: string
  updated_at: string
}

export interface Offboarding {
  id: number
  organization_id: number
  organization_name: string
  user_id: number | null
  user_email: string | null
  type: 'cancel subscription' | 'downgrade to free' | 'delete account'
  reasons: Record<string, string>
  created_at: string
  updated_at: string
}

export interface PassportVerification {
  id: number
  case_id: number
  form: PassportVerificationForm
  mrz: string
  status: string
}

export interface PassportVerificationForm {
  country: string
  date_of_birth: string
  dateOfExpiry: string
  documentNumber: string
  gender: string
  givenNames: string
  lastName: string
  nationality: string
  optionalData: string
}

export interface Permission {
  id: number
  name: string
  created_at: string
  updated_at: string
}

export interface PermissionWithRoles extends Permission {
  roles: Role[]
}

export interface RiskConfig {
  client_risk_method: ClientRiskMethod
  client_risk_screening_weight: number
  client_risk_onboarding_weight: number
  id: number
  organization_id: number
  threshold: number
  country_profile: RiskConfigCountryProfile
  created_at: string
  updated_at: string
}

export interface RiskConfigCountryProfile {
  name: string
  nullWeight: number
  countries: Record<string, number | null>
}

export type RiskSourceConfigWeight = Record<string, number>
export type RiskSourceConfigWeights = Record<string, RiskSourceConfigWeight>

export interface RiskSourceConfig {
  id: number
  organization_id: number
  source: string
  impact: number
  minimum_probability: number
  fields: string[]
  weights: RiskSourceConfigWeights
  created_at: string
  updated_at: string
}

export interface Role {
  id: number
  name: string
  created_at: string
  updated_at: string
  permissions: Permission[]
}

export interface SearchResponse<T = any> {
  data: T[]
  meta: { total: number; current_page: number; per_page: number; last_page: number; to: number; from: number }
}

export interface StatisticsResponse<T = any> {
  data: T[]
  meta?: { interval: string }
}

export interface ContentsquareUserId {
  id: number
  user_id: number
  contentsquare_user_id: string
}

export interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  name: string
  created_at: string
  updated_at: string
  groups: Group[]
  roles: Role[]
  contentsquare_user_ids?: ContentsquareUserId[]
  pivot: UserPivot
  password_reset_tokens?: { created_at: string; email: string }[]
  one_time_password_required?: boolean
}

export interface UserInvitation {
  id: number
  uuid: string
  user: User
  user_id: number
  organization_id: number
  expires_at: string
  created_at: string
  updated_at: string
  role_names: string[]
  group_ids: number[]
}

export interface SignupToken {
  id: number
  uuid: string
  organization_id: number
  azure_tenant_id: string | null
  hubspot_integration_requested: boolean
  created_from_hyperline: boolean
  created_at: string
  updated_at: string
}

export interface OrganizationFunction {
  id: number
  organization_id: number
  name: string
}

export interface UserPivot {
  user_id: number
  organization_id: number
  organization_function_id: number | null
  active: boolean
  deleted_at: null | string
}

export interface UserDetailed extends Omit<User, 'pivot'> {
  roles: Role[]
  organizations: Organization<['policy', 'pivot']>[]
  password_reset_tokens: { created_at: string; email: string }[]
  tokens_count: number
  settings: UserSettings
}

export interface Group {
  id: number
  name: string
}

export const ORGANIZATION_INDUSTRIES = [
  'Aerospace & Defence',
  'Agriculture & Food',
  'Asset & Wealth Management',
  'Automotive & Assembly',
  'Banking',
  'Chemicals',
  'Education',
  'Electronics & Semiconductor',
  'Energy, Utilities & Resources',
  'Engineering and Construction',
  'Entertainment & Media',
  'Financial Services',
  'Health Care',
  'Industrial Manufacturing',
  'Insurance',
  'Investment Management',
  'Law & Legal Services',
  'Maritime',
  'Marketing',
  'Mining & Metals',
  'Pension Sector',
  'Pharma & Life Sciences',
  'Real Estate',
  'Retail & Customer',
  'Software',
  'Technology',
  'Trade',
  'Telecommunication',
  'Transport & Logistics',
  'Trust Management',
  'Other',
] as const

export type PlanManagementTextType = 'plan' | 'support_plan'
export type PlanManagementTextPlan = 'free' | 'premium' | 'enterprise'
export type SubscriptionTemplate = 'free' | 'premium_annual' | 'premium_monthly'
export type SubscriptionTemplateFilter = SubscriptionTemplate | null

export interface CustomText {
  id: number
  key: string
  value: string
  sort_order: number
  created_at: string
  updated_at: string
}

export interface PlanManagementTextContent {
  description: string
  included_description: string
  lines: string[]
  monthly_price: null | string
  annual_price: null | string
  annual_savings: null | string
}

interface OrganizationRelations {
  policy: { policy: OrganizationPolicy | null }
  pivot: { pivot: UserPivot }
}

export type Organization<With extends (keyof OrganizationRelations)[] = []> = {
  id: number
  name: string
  hyperline_customer_id: string | null
  hyperline_subscription_template: SubscriptionTemplate | null
  products_editable: boolean
  address: null | string
  country: null | string
  industry: null | string
  city: null | string
  postal_code: null | string
  created_at: string
  updated_at: string
  contract_start: string
  contract_end: string | null
  subscription_initial_billing_at: string | null
  unit_limit_period: 'daily' | 'weekly' | 'monthly' | 'yearly' | '2-years' | '3-years' | null
  unit_limit_period_started_at?: string | null
  unit_limit_period_ends_at?: string | null
  origin: null | string
  deleted_at: null | string
  has_onboarding: boolean
  has_implicit_feedback: boolean
  has_data_directory: boolean
  has_passport_verification: boolean
  has_datachecker: boolean
  has_transaction_monitoring: boolean
  last_activity_at: string | null
  unit_limit_searches: number | null
  unit_limit_case_creations: number | null
  unit_limit_active_cases: number | null
  unit_limit_identity_verifications: number | null
  unit_limit_client_creations: number | null
  unit_limit_form_creations: number | null
  unit_limit_transaction_creations: number | null
  unit_limit_active_clients: number | null
  unit_limit_active_form_templates: number | null
  unit_limit_active_data_directory_entries: number | null
  unit_limit_active_users: number | null
  unit_limit_usage?: Partial<Record<UnitLimitType, number>>
  account_access_consent: boolean
  essential_tier_vendors: string[]
  third_party_tier_vendors: string[]
  ai_tier_vendors: string[]
  support_plan: 'free' | 'premium' | 'enterprise'
  has_veriff?: boolean
  has_hubspot?: boolean
  signup_token: null | SignupToken
} & WithRelations<OrganizationRelations, With>

export interface ManagementHubspotObject {
  pascal_organization: string
  hs_object_id: string
  createdate: string // cspell: disable-line
  hs_lastmodifieddate: string // cspell: disable-line
}

interface BaseSpectrumLog {
  id: number
  created_at: string
  data: Record<never, never>
  email: string
  updated_at: string
  user_id: number
  action: string
}

interface SpectrumLogUnknown extends BaseSpectrumLog {
  action: 'unknown'
}

interface SpectrumLogRoleAssign extends BaseSpectrumLog {
  action: 'role:assign'
  data: { model_id: number; role_id: number }
}

interface SpectrumLogRoleRemove extends BaseSpectrumLog {
  action: 'role:remove'
  data: { model_id: number; role_id: number }
}

export type SpectrumLog = SpectrumLogUnknown | SpectrumLogRoleAssign | SpectrumLogRoleRemove

interface BaseOrganizationAudit {
  id: number
  organization_id: number
  user_id: number[]
  created_at: string
  updated_at: string
  description: string
  type: string
  data: null | Record<never, never>
  by_app_employee: boolean
  ip_address: string
  user_name: string
}

export interface GroupUsersChangedOrganizationAudit extends BaseOrganizationAudit {
  type: 'group users changed'
  data: {
    group: string
    emails: string[]
  }
}

export interface PoliciesChangedOrganizationAudit extends BaseOrganizationAudit {
  type: 'policies changed'
  data: null | { old: Partial<OrganizationPolicy>; new: Partial<OrganizationPolicy> }
}

export interface ServiceDisabledOrganizationAudit extends BaseOrganizationAudit {
  type: 'service disabled'
  data: { service: string }
}

export interface ServiceEnabledOrganizationAudit extends BaseOrganizationAudit {
  type: 'service enabled'
  data: { service: string }
}

export interface GroupCreatedOrganizationAudit extends BaseOrganizationAudit {
  type: 'group created'
  data: { name: string }
}

export interface GroupDeletedOrganizationAudit extends BaseOrganizationAudit {
  type: 'group deleted'
  data: { name: string }
}

export interface UserCreatedOrganizationAudit extends BaseOrganizationAudit {
  type: 'user created'
  data: { email: string }
}

export interface UserDeletedOrganizationAudit extends BaseOrganizationAudit {
  type: 'user deleted'
  data: {
    email: string
    cases_deleted?: boolean
    nr_of_cases_deleted?: number
    new_case_assignee_email?: string
    new_case_assignee_group_name?: string
    client_assignee_remove?: boolean
  }
}

export interface UserDeactivatedOrganizationAudit extends BaseOrganizationAudit {
  type: 'user deactivated'
  data: { email: string; cases_deleted?: boolean; nr_of_cases_deleted?: number; new_case_assignee_email?: string; new_case_assignee_group_name?: string }
}

export interface UserEmailChangedOrganizationAudit extends BaseOrganizationAudit {
  type: 'user email changed'
  data: { old_email: string; new_email: string }
}

export interface UserFirstNameChangedOrganizationAudit extends BaseOrganizationAudit {
  type: 'user first name changed'
  data: { first_name: string }
}

export interface UserLastNameChangedOrganizationAudit extends BaseOrganizationAudit {
  type: 'user last name changed'
  data: { last_name: string }
}

export interface UserGroupsChangedOrganizationAudit extends BaseOrganizationAudit {
  type: 'user groups changed'
  data: { email: string; groups: string[] }
}

export interface UserRestoredOrganizationAudit extends BaseOrganizationAudit {
  type: 'user restored'
  data: { email: string }
}

export interface UserRolesChangedOrganizationAudit extends BaseOrganizationAudit {
  type: 'user roles changed'
  data: { email: string; roles: string[] }
}
export interface MonitoringFrequencyChangedOrganizationAudit extends BaseOrganizationAudit {
  type: 'monitoring frequency changed'
  data: { monitoring_frequency: MonitoringFrequency }
}

export interface PacksActivatedOrganizationAudit extends BaseOrganizationAudit {
  type: 'data sources activated'
  data: { sources: string[] }
}
export interface PacksDeactivatedOrganizationAudit extends BaseOrganizationAudit {
  type: 'data sources deactivated'
  data: { sources: string[] }
}

export interface ProductsAddedOrganizationAudit extends BaseOrganizationAudit {
  type: 'products added'
  data: { products: string[] }
}

export interface ProductsRemovedOrganizationAudit extends BaseOrganizationAudit {
  type: 'products removed'
  data: { products: string[]; removal_date: string | null }
}

export interface ProductRemovalCancelledOrganizationAudit extends BaseOrganizationAudit {
  type: 'product removal cancelled'
  data: { products: string[] }
}

export interface DetailsChangedOrganizationAudit extends BaseOrganizationAudit {
  type: 'details changed'
  data: { name?: string; address?: string; country?: string; industry?: string; city?: string; postal_code?: string }
}

export interface ClientDeletedOrganizationAudit extends BaseOrganizationAudit {
  type: 'client deleted'
  data: { cases_deleted?: boolean }
}

export interface IntegrationsChangedOrganizationAudit extends BaseOrganizationAudit {
  type: 'integrations changed'
  data: null
}

export interface TransactionsImportedOrganizationAudit extends BaseOrganizationAudit {
  type: 'transactions imported'
  data: { count: number }
}

export interface GlobalExemptionsImportedOrganizationAudit extends BaseOrganizationAudit {
  type: 'global exemptions imported'
  data: { accounts: string[] }
}

export interface GlobalExemptionsDeletedOrganizationAudit extends BaseOrganizationAudit {
  type: 'global exemptions deleted'
  data: { accounts: string[] }
}

export type OrganizationAudit =
  | GroupUsersChangedOrganizationAudit
  | PoliciesChangedOrganizationAudit
  | ServiceDisabledOrganizationAudit
  | ServiceEnabledOrganizationAudit
  | UserCreatedOrganizationAudit
  | UserDeletedOrganizationAudit
  | UserDeactivatedOrganizationAudit
  | GroupCreatedOrganizationAudit
  | GroupDeletedOrganizationAudit
  | UserEmailChangedOrganizationAudit
  | UserFirstNameChangedOrganizationAudit
  | UserLastNameChangedOrganizationAudit
  | UserGroupsChangedOrganizationAudit
  | UserRestoredOrganizationAudit
  | UserRolesChangedOrganizationAudit
  | MonitoringFrequencyChangedOrganizationAudit
  | PacksActivatedOrganizationAudit
  | PacksDeactivatedOrganizationAudit
  | ProductsAddedOrganizationAudit
  | ProductsRemovedOrganizationAudit
  | ProductRemovalCancelledOrganizationAudit
  | DetailsChangedOrganizationAudit
  | ClientDeletedOrganizationAudit
  | IntegrationsChangedOrganizationAudit
  | TransactionsImportedOrganizationAudit
  | GlobalExemptionsImportedOrganizationAudit
  | GlobalExemptionsDeletedOrganizationAudit

export interface OrganizationAuditBucket {
  key?: string
  count: number
  data: Record<string, any> | null
  year: number
  month: number
  day: number
  by_app_employee: boolean
  created_at: string
  type: string
  user_id: number
  user_name: string
  description?: string
}

export interface HubSpotFieldMapping {
  person?: {
    nationalities?: string[] | null
    date_of_birth?: string | null
    country_of_birth?: string | null
    country_of_residence?: string | null
    gender?: string | null
    additional_terms?: string[] | null
  }
  business?: {
    country?: string[] | null
    company_number?: string | null
    address?: string | null
    additional_terms?: string[] | null
  }
}

export interface OrganizationIntegration {
  id: number
  azure_tenant_id: string
  azure_require_user_request: boolean
  azure_user_request_via: string[]
  hubspot_portal_id: null | number
  hubspot_user_id: null | number
  hubspot_refresh_token: null | string
  hubspot_refresh_token_failures: number
  hubspot_auto_search: boolean
  hubspot_property_sync: boolean
  hubspot_user_sync: boolean
  hubspot_default_case_status: 'Preview' | 'In review'
  hubspot_report_initial_screening_enabled: boolean
  hubspot_report_manual_generation_enabled: boolean
  hubspot_report_user_screening_new_hits_enabled: boolean
  hubspot_report_automated_screening_new_hits_enabled: boolean
  hubspot_field_mapping: HubSpotFieldMapping
  webhook_event_case_created_enabled: boolean
  webhook_event_case_created_urls: string[]
  webhook_event_case_searched_enabled: boolean
  webhook_event_case_searched_urls: string[]
  webhook_event_case_hits_found_enabled: boolean
  webhook_event_case_hits_found_urls: string[]
  webhook_event_case_resolved_enabled: boolean
  webhook_event_case_resolved_urls: string[]
  webhook_event_client_review_status_changed_enabled: boolean
  webhook_event_client_review_status_changed_urls: string[]
}

export type NewsAdverseAndNegativeEventsFilter = null | 'adverse' | 'negative_events'

export interface OrganizationPolicy {
  id: number
  organization_id: number
  default_case_sources_person: CaseSourceName[]
  default_case_sources_business: CaseSourceName[]
  default_case_sources_asset: CaseSourceName[]
  default_confidence: 'Relative' | 'Exact'
  default_match_on_connections: boolean
  confidence_threshold_enforcements: number
  confidence_threshold_news: number
  confidence_threshold_other: number
  confidence_threshold_peps: number
  confidence_threshold_sanctions: number
  news_adverse_and_negative_events_filter: NewsAdverseAndNegativeEventsFilter
  news_age_years_filter: null | number
  news_country_filter: string[]
  preview_default_case_sources_person: CaseSourceName[]
  preview_default_case_sources_business: CaseSourceName[]
  preview_default_case_sources_asset: CaseSourceName[]
  preview_confidence_threshold_enforcements: number
  preview_confidence_threshold_news: number
  preview_confidence_threshold_other: number
  preview_confidence_threshold_peps: number
  preview_confidence_threshold_sanctions: number
  preview_news_adverse_and_negative_events_filter: NewsAdverseAndNegativeEventsFilter
  preview_news_age_years_filter: null | number
  preview_news_country_filter: string[]
  monitoring_frequency_sanctions: MonitoringFrequency
  monitoring_frequency_peps: MonitoringFrequency
  monitoring_frequency_news: MonitoringFrequency
  monitoring_frequency_enforcements: MonitoringFrequency
  monitoring_frequency_other: MonitoringFrequency
  edit_cases: string
  hit_update_fields: string[]
  read_cases: string
  require_hit_comments: boolean
  hit_comment_options: string[]
  default_assignee_type: 'self' | 'user' | 'group'
  default_assignee_user_id: number | null
  default_assignee_group_id: number | null
  require_one_time_password: boolean
  allow_id_document_downloads: boolean
  external_users_language: Locale
  bank_accounts_dormant_after: null | number
  timezone: string
  eod_alert_buffer: number
  third_party_tier_enabled: boolean
  ai_tier_enabled: boolean
  ignore_intra_client_transactions: boolean
  default_approval_template_very_low_id: number | null
  default_approval_template_low_id: number | null
  default_approval_template_moderate_id: number | null
  default_approval_template_high_id: number | null
  default_approval_template_very_high_id: number | null
  block_lower_risk_approval_templates: boolean
  required_fields_person: string[]
  required_fields_business: string[]
  required_fields_asset: string[]
  created_at: string
  updated_at: string
}

export type ConfidenceThresholdKey =
  | 'confidence_threshold_enforcements'
  | 'confidence_threshold_news'
  | 'confidence_threshold_sanctions'
  | 'confidence_threshold_peps'
  | 'confidence_threshold_other'
  | 'preview_confidence_threshold_enforcements'
  | 'preview_confidence_threshold_news'
  | 'preview_confidence_threshold_sanctions'
  | 'preview_confidence_threshold_peps'
  | 'preview_confidence_threshold_other'

export type ConfidenceThresholds = Pick<OrganizationPolicy, ConfidenceThresholdKey>

export type MonitoringFrequencyKey =
  | 'monitoring_frequency_sanctions'
  | 'monitoring_frequency_peps'
  | 'monitoring_frequency_enforcements'
  | 'monitoring_frequency_news'
  | 'monitoring_frequency_other'

export type CaseServiceName = 'privateWatchlist' | 'passportVerification' | 'implicitFeedback'

export interface Env {
  app_env: string
  app_name: string
  file_upload_all_extensions: string[]
  file_upload_image_extensions: string[]
  import_file_limits: {
    max_size_mb: number
    max_rows: number
  }
  browser: {
    name: string
    version: string
  }
  canny: {
    url: string
    url_feature_requests: string
    app_id: string
  }
  contentsquare: {
    tag_id: string
  }
  datachecker: {
    retention_seconds: number
    desktop_mode: boolean
  }
  hubspot: {
    meetings_url: string
  }
  mapbox: {
    token: string | null
  }
  sentry: {
    dsn: string | null
  }
  statuspage: {
    url: string
  }
  userpilot: {
    token: string | null
  }
  version: string
}

interface PeerGroupRelations {
  clientFilter: { client_filter: ClientFilter }
}

export type PeerGroup<With extends (keyof PeerGroupRelations)[] = []> = {
  id: number
  organization_id: number
  name: string
  created_at: string
  updated_at: string
  estimated_incoming_amount: number | null
  estimated_incoming_currency: string | null
  estimated_outgoing_amount: number | null
  estimated_outgoing_currency: string | null
  cash_incoming_amount: number | null
  cash_incoming_currency: string | null
  cash_outgoing_amount: number | null
  cash_outgoing_currency: string | null
  cash_percentage: number | null
  transaction_count: number | null
  transaction_volume_incoming: number | null
  transaction_volume_outgoing: number | null
  transaction_count_cross_border: number | null
  transaction_volume_cross_border_incoming: number | null
  transaction_volume_cross_border_outgoing: number | null
  transaction_countries: string[]
  allowed_deviation_percentage: number | null
  client_filter?: ClientFilter
} & WithRelations<PeerGroupRelations, With>

export interface StandardIndustryClassification {
  id: number
  organization_id: number
  code: string
  description: string
  risk: number
  created_at: string
  updated_at: string
}

interface LabelRelations {
  clients_count: { clients_count: number }
  cases_count: { cases_count: number }
  case_hits_count: { case_hits_count: number }
}

export type Label<With extends (keyof LabelRelations)[] = []> = {
  id: number
  organization_id: number
  name: string
  index: number
  color: string
  created_at: string
  updated_at: string
} & WithRelations<LabelRelations, With>

export type TransactionStatus = 'client unknown' | 'match required' | 'in review' | 'waiting for approval' | 'approved' | 'declined'

interface TransactionRelations {
  counterPartyClients: { counter_party_clients: Pick<Client, 'id' | 'name'>[] }
}

export type Transaction<With extends (keyof TransactionRelations)[] = []> = {
  id: number
  identifier: string | number
  is_related?: boolean
  created_at: string
  updated_at: string
  transaction_date: string
  direction: TransactionDirection
  client_account_number: string
  client_swift_code: string | null
  client_bank_account_country: string | null
  organization_id: number
  user_id: number
  amount: number
  currency: string
  status: TransactionStatus
  approval_steps?: ApprovalStep[]
  description: string
  counter_party_name: string | null
  counter_party_currency: string | null
  counter_party_account_number: string | null
  counter_party_swift_code: string
  counter_party_bank_name: string | null
  counter_party_bank_account_country: string | null
  type: string | null
} & WithRelations<TransactionRelations, With>

export type TransactionAnalysisTransaction = Transaction & {
  name: string
  client_type: Client['type']
}

export interface TransactionAnalysisGroup {
  amount: number
  client_id: number
  client_type: Client['type']
  count: number
  name: string
  transaction_date: string
  unique_counter_parties: number
}

export interface CurrencyInfo {
  alphabeticCode: string
  currency: string
  minorUnit: number
  numericCode: number
  symbol: string
}

export interface PersonalAccessToken {
  name: string
  id: number
  expires_at: string | null
  created_at: string
  updated_at: string
  last_used_at: string | null
  abilities: string[]
}

export type ReportType = 'case' | 'cases' | 'preview' | 'client' | 'organization-audit' | 'user-sessions' | 'transaction-report'
export type ReportSettingsType = Exclude<ReportType, 'transaction-report'>

export interface Report {
  id: number
  name: string
  user_id: number
  organization_id: number
  type: Exclude<ReportType, 'cases' | 'preview'>
  created_at: string
  updated_at: string
  batch_id: string | null
  batch_name: string | null
}

export interface ReportSchedule {
  id: number
  user_id: number
  reportable_id: number
  created_at: string
  updated_at: string
  scheduled_at: string
}

export interface ReportSettings {
  id: number
  user_id: number
  type: ReportSettingsType
  pages: string[]
  detailed: boolean | null
  include_case_reports: boolean | null
  case_report_pages: string[] | null
}

export interface Session {
  id: number
  ip_address: string
  last_activity: number
  user_agent: string
  user_id: number
  browser: {
    name: string
    version: string
    os: string
  }
}

export interface SortEvent {
  prop: string
  order: SortOrder
}

export interface DashboardPanelPosition {
  h: number
  w: number
  x: number
  y: number
}

export interface DashboardPanel extends DashboardPanelPosition {
  id: number
  dashboard_id: number
  title: string
  metric: string
  chart_type: string
  time_from_days: number | null
  group_id: number[] | null
  client_id: number[] | null
  created_at: string
  updated_at: string
}

export interface Dashboard {
  id: number
  index: number
  user_id: number
  title: string
  panels: DashboardPanel[]
  group_id: number[] | null
  client_id: number[] | null
  time_from_days: number | null
  created_at: string
  updated_at: string
}

export interface UserSettings {
  id?: number
  application_update_email: boolean
  date_format: string
  number_format: string
  interface_language: Locale
  known_languages: TranslationLanguage[]
  notify_hit_updates: string
  send_empty_monitor_report: boolean
  show_live_chat: boolean
  summary_report_day_of_week: number
  summary_report_email: boolean
  summary_report_interval: string
  translation_language: TranslationLanguage
  require_one_time_password: boolean
  receive_transaction_reports_via_email: boolean
  receive_transaction_reports_without_findings: boolean
  comment_mentions_email_frequency: 'daily' | 'weekly' | 'never'
}

export interface OrganizationDefaultSettings {
  id?: number
  organization_id?: number
  interface_language: Locale
  translation_language: TranslationLanguage
  known_languages: TranslationLanguage[]
  date_format: string
  created_at?: string
  updated_at?: string
}

export interface WebhookEvent {
  id: number
  organization_id: number
  url: string
  payload: Record<string, any>
  response_status: number | null
  error: string | null
  sent_at: string
  created_at: string
  updated_at: string
}

export type UnitLimitType =
  | 'unit_limit_searches'
  | 'unit_limit_case_creations'
  | 'unit_limit_active_cases'
  | 'unit_limit_identity_verifications'
  | 'unit_limit_client_creations'
  | 'unit_limit_form_creations'
  | 'unit_limit_transaction_creations'
  | 'unit_limit_active_clients'
  | 'unit_limit_active_form_templates'
  | 'unit_limit_active_data_directory_entries'
  | 'unit_limit_active_users'

export interface UnitLimitsUpdatedEvent {
  unitLimits: Partial<Record<UnitLimitType, { limit: number | null; usage: number }>>
  unitLimitPeriodStartedAt: string | null
  unitLimitPeriodEndsAt: string | null
}

export interface AsyncCache {
  name: string
  has(key: unknown): Promise<boolean>
  get<T = any, D = null>(key: string, defaultValue?: D): Promise<T | D>
  put(key: string, value: unknown, ttl?: Dayjs): Promise<void>
  remember<T = any>(key: string, ttl: Dayjs, callback: () => T | Promise<T>): Promise<T>
  forget(key: string): Promise<void>
  list(): Promise<Record<string, string>>
  flush(): Promise<void>
  flushExpired(): Promise<void>
}

export interface SyncCache {
  name: string
  has(key: unknown): boolean
  get<T = any, D = null>(key: string, defaultValue?: D): T | D
  put(key: string, value: unknown, ttl?: Dayjs): void
  remember<T = any>(key: string, ttl: Dayjs, callback: () => T | Promise<T>): Promise<T>
  forget(key: string): void
  list(): Record<string, string>
  flush(): void
  flushExpired(): void
}

interface OrganizationUserRelations {
  organization: { organization: Organization }
  roles: { roles: Role[] }
  user: { user: User & { tokens_exists: boolean } }
}

export type OrganizationUser<With extends (keyof OrganizationUserRelations)[] = []> = {
  organization_id: number
  organization_function_id: number | null
  user_id: number
  created_at: string
  updated_at: string
  deleted_at: string | null
} & WithRelations<OrganizationUserRelations, With>
